import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  TextField,
  useTheme,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import { AiEventType, ConnectionStatus } from "../../types/ai-event";
import type { QueuedAiMessage } from "../../slices/ui";

interface AiPanelInputProps {
  // false means the socket refused the message (useSendMessage); void or true means it went out.
  onSend: (content: string) => boolean | void;
  onInterrupt: () => void;
  connectionStatus: ConnectionStatus;
  isRunning: boolean;
  disabled?: boolean;
  // A chat lifecycle other than idle or running holds the composer (resuming, loading, reconnecting, offline).
  hold?: boolean;
  // Messages sent while a turn runs, oldest first (state.ui.aiQueued). The thread draws them as bubbles.
  queued: QueuedAiMessage[];
  onQueue: (content: string) => void;
  onDequeue: () => void;
  // The newest event's type: an accepted send waits for the service to report running, and some events end that wait.
  lastEventType?: AiEventType;
}

export const AiPanelInput: React.FC<AiPanelInputProps> = ({
  onSend,
  onInterrupt,
  connectionStatus,
  isRunning,
  disabled,
  hold,
  queued,
  onQueue,
  onDequeue,
  lastEventType,
}) => {
  const theme = useTheme();
  const [input, setInput] = useState("");
  const held = !!disabled || !!hold || connectionStatus !== ConnectionStatus.CONNECTED;

  // isRunning comes from the events and a user echo does not set it, so after a send there is a window where
  // it is still false. awaitingRun covers that window: no second message goes out until the service reports
  // running (or an error or idle ends the wait), because the service refuses a message while a turn runs.
  const awaitingRun = useRef(false);
  const dispatchSend = useCallback(
    (content: string) => {
      awaitingRun.current = onSend(content) !== false;
    },
    [onSend]
  );

  // Declared before the drain effect so a releasing event clears the wait in the same commit that drains.
  useEffect(() => {
    if (
      isRunning ||
      held ||
      lastEventType === AiEventType.SESSION_ERROR ||
      lastEventType === AiEventType.CONNECTION_ERROR ||
      lastEventType === AiEventType.STATUS_IDLE ||
      lastEventType === AiEventType.STATUS_TERMINATED
    ) {
      awaitingRun.current = false;
    }
  }, [isRunning, held, lastEventType]);

  // One message per turn, in order. Stop ends the turn, so it releases the first queued message too.
  useEffect(() => {
    if (queued.length === 0 || isRunning || held || awaitingRun.current) return;
    const [next] = queued;
    onDequeue();
    dispatchSend(next.content);
  }, [queued, isRunning, held, lastEventType, onDequeue, dispatchSend]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || held) return;
    if (isRunning || queued.length > 0 || awaitingRun.current) onQueue(trimmed);
    else dispatchSend(trimmed);
    setInput("");
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1.5,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <TextField
        inputProps={{ "data-testid": "ai-input" }}
        fullWidth
        size="small"
        multiline
        maxRows={8}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e as unknown as React.FormEvent);
          }
        }}
        placeholder={
          held
            ? connectionStatus === ConnectionStatus.CONNECTED
              ? "Cas is working..."
              : "Connecting to Cas..."
            : "Ask Cas..."
        }
        value={input}
        onChange={(e) => setInput(e.target.value)}
        disabled={held}
        autoComplete="off"
        sx={{
          "& .MuiOutlinedInput-root": {
            fontSize: "0.8rem",
          },
        }}
      />
      {isRunning ? (
        <IconButton data-testid="ai-stop" aria-label="Stop" size="small" onClick={onInterrupt} sx={{ color: theme.palette.text.secondary }}>
          <StopIcon fontSize="small" />
        </IconButton>
      ) : (
        <IconButton
          data-testid="ai-send"
          size="small"
          type="submit"
          disabled={held || !input.trim()}
          color="primary"
          sx={held ? { opacity: 0.4 } : undefined}
        >
          <SendIcon />
        </IconButton>
      )}
    </Box>
  );
};
